import { CONSENT, isConsentGranted } from "./consent"
import Cookies from "./cookies"

declare global {
  interface Window {
    // https://documentation.bloomreach.com/data-hub/docs/sdk-web-tracking
    brweb?: {
      identify(customerIds: object, customerProperties?: object): void
      track(
        eventName: string,
        eventProperties?: EventProperties,
        // SDK uses Beacon API on `pagehide` but we're told by Bloomreach not to rely on that, hence
        // callbacks to track events preceding a redirect.
        successCallback?: () => void,
        errorCallback?: () => void
      ): void
    }
  }
}

type EventProperties = Record<string, unknown>

const EMAIL_COOKIE = "email_address"
const NAVIGATION_TIMEOUT = 500

const identify = (): void => {
  if (!window.brweb || !isConsentGranted(CONSENT.TARGETING)) {
    return
  }

  const email = Cookies.get(EMAIL_COOKIE)?.trim().toLowerCase()

  if (!email) {
    return
  }

  window.brweb.identify({ email_id: email })
}

const track = (
  eventName: string,
  eventProperties?: EventProperties,
  successCallback?: () => void,
  errorCallback?: () => void
): boolean => {
  if (!window.brweb || !isConsentGranted(CONSENT.TARGETING)) {
    return false
  } else {
    window.brweb.track(eventName, eventProperties, successCallback, errorCallback)
    return true // Reflects whether tracking is available, not whether it succeeded or failed.
  }
}

const trackOnLinkClick = (
  eventName: string,
  eventProperties: EventProperties,
  event: MouseEvent
): void => {
  const { href } = event.currentTarget as HTMLAnchorElement
  const navigate = (): void => window.location.assign(href)

  // If tracking is available, prevent link click so tracking can proceed before unload.
  // Navigation will occur on either success or failure, but also set a timeout in case neither
  // callback fires.
  if (track(eventName, eventProperties, navigate, navigate)) {
    event.preventDefault()
    window.setTimeout(navigate, NAVIGATION_TIMEOUT)
  }
}

const viewItemEvent = (eventProperties: EventProperties): void => {
  track("view_item", eventProperties)
}

const viewItemEventOnLinkClick = (eventProperties: EventProperties, event: MouseEvent): void => {
  trackOnLinkClick("view_item", eventProperties, event)
}

export { identify, viewItemEvent, viewItemEventOnLinkClick }
